import React, { useState, useEffect } from 'react';
import { useLanguagesContext } from '../../contexts/LanguagesContext';
import Select from 'react-select';
import Button from '../general/Button';
import useCollections from '../../hooks/useCollections';
import useDecks from '../../hooks/useDecks';

const CollectionLanguages = ({collection, onLanguagesChange}) => {

    const [chosenLanguages, setChosenLanguages] = useState(collection.languages);
    const {allLanguages, sourceLanguageOptions} = useLanguagesContext();
    const {saveChangesToCollection} = useCollections();
    const {getAllMultilingualDecksFromUser} = useDecks();
    const [saving, setSaving] = useState(false);
    const [availableDecks, setAvailableDecks] = useState([]);

    const addLanguagesFromDeck = React.useCallback((deck) => {
        if (!deck || !deck.languages) return;
        const deckLanguages = deck.languages.filter(lang => lang !== null && lang !== "");
        setChosenLanguages(prevLanguages => {
            const newLanguages = [...prevLanguages];
            deckLanguages.forEach((language) => {
                if (!newLanguages.includes(language)){
                    newLanguages.push(language);
                }
            });
            return newLanguages;
        });
    }, []);

    // Expose function to add languages from outside
    useEffect(() => {
        if (onLanguagesChange) {
            onLanguagesChange({
                addLanguagesFromDeck: addLanguagesFromDeck
            });
        }
    }, [onLanguagesChange, addLanguagesFromDeck]);

    const languageOnChange = (option, index) => {
        let newLanguages = [...chosenLanguages];
        newLanguages[index] = option.value;
        setChosenLanguages(newLanguages);
    }

    const saveChangesClick = async () => {
        setSaving(true);
        await saveChangesToCollection({languages: chosenLanguages, docId: collection.docId});
        setSaving(false);
    }

    useEffect(() => {
        const fetchDecks = async () => {
            const decks = await getAllMultilingualDecksFromUser();
            setAvailableDecks(decks);
        };
        if (getAllMultilingualDecksFromUser) {
            fetchDecks();
        }
    }, [getAllMultilingualDecksFromUser]);

    useEffect(() => {
        if (collection && collection.languages) {
            setChosenLanguages(collection.languages);
        }
    }, [collection]);

    const addDefaultLanguages = () => {
        const list = ["en", "fr", "no", "es", "zh", "de", "it", "nl", "da", "sv", "pt", "fi", "ro", "is", "ru"];
       // add if not already present
        const newLanguages = [...chosenLanguages];
        list.forEach((language) => {
            if (!newLanguages.includes(language)){
                newLanguages.push(language);
            }
        });
        setChosenLanguages(newLanguages);
    }

    const addAllDeckLanguagesToCollection = (deckId) => {
        const deck = availableDecks.find(d => d.id === parseInt(deckId));
        if (!deck || !deck.languages) return;
        
        const deckLanguages = deck.languages.filter(lang => lang !== null && lang !== "");
        const newLanguages = [...chosenLanguages];
        
        deckLanguages.forEach((language) => {
            if (!newLanguages.includes(language)){
                newLanguages.push(language);
            }
        });
        setChosenLanguages(newLanguages);
    }

    return (
        <>
            <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-3">
                    <h1>Languages in collection</h1>
                    <div className="flex flex-row justify-center gap-5">
                        <button
                            onClick={addDefaultLanguages}
                            className="px-4 py-2 bg-persian-green text-white rounded-lg shadow-md hover:bg-opacity-90 transition"
                        >
                            Add default languages
                        </button>
                    </div>
                </div>
                {chosenLanguages &&
                    <>
                    {chosenLanguages.length === 0 ?
                        <div className="text-center">
                            No languages added yet
                        </div>
                    :
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                        {chosenLanguages.map((language, index) => (
                            <div key={index} className="flex flex-col gap-1">
                                <div className="text-xs muted-text">
                                    #{index+1}
                                </div>
                                <Select 
                                    value={sourceLanguageOptions[1][language]}
                                    onChange={(option)=>languageOnChange(option, index)}
                                    options={sourceLanguageOptions[0]}
                                    autoFocus={false}
                                    className="text-gray-500 dark:text-gray-300 text-sm"
                                />
                            </div>
                        ))}
                    </div>
                    }
                    {collection.decks && collection.decks.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                            <div className="text-sm font-semibold mb-2">Add languages from decks:</div>
                            <div className="flex flex-wrap gap-2">
                                {collection.decks.map((deckId) => {
                                    const deck = availableDecks.find(d => d.id === parseInt(deckId));
                                    if (!deck || !deck.languages) return null;
                                    const deckLanguages = deck.languages.filter(lang => lang !== null && lang !== "");
                                    const missingLanguages = deckLanguages.filter(lang => !chosenLanguages.includes(lang));
                                    if (missingLanguages.length === 0) return null;
                                    return (
                                        <button
                                            key={deckId}
                                            onClick={() => addAllDeckLanguagesToCollection(deckId)}
                                            className="px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition"
                                        >
                                            Add all from "{deck.name}" ({missingLanguages.length} missing)
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div className="flex flex-row justify-center gap-5">
                        <Button text="Add Language" color="charcoal" onClick={()=>setChosenLanguages([...chosenLanguages, ""])} />
                        <Button text={!saving ? "Save Changes" : "Saving..."} color="green" onClick={saveChangesClick} />
                    </div>
                    </>
                }

            </div>
        </>
    )

}

export default CollectionLanguages;